import React, { useEffect, useState } from 'react';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { useSession } from '@/hooks/useSession';
import { FiCamera } from 'react-icons/fi';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import promiseRequest from '@/utils/promiseToast';
import Onboarding from '@/components/custom/onboarding';
import { Loader } from '@/components/custom/loader';
import { validateCPF } from '@/utils/validateCPF';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DashboardContent } from '@/layouts/dashboard';
import { PageContent, Page, PageSidebar } from '@/components/custom/page';
import { TypographyDisclaimer } from '@/components/custom/typography';
import { AvatarWrapper } from '@/components/ui/avatar';
import { Label } from '@/components/ui/label';
import { Input, InputMask, InputPassword } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ProfileForm, ProfileInfo, ProfileSidebar } from '@/components/custom/profile';
import { FormCheck, FormField, FormSwitch } from '@/components/custom/form';
import { Textarea } from '@/components/ui/textarea';

export function Profile(props) {
  const { t } = useTranslation();
  const { promiseToast, isSubmitting } = promiseRequest();
  const { session, sanitizeResponse, setSession } = useSession();
  const client = useClient();
  const [values, setValues] = useState({
    name: session.user?.name,
    email: session.user?.email,
    bio: session.user?.bio,
    phone: session.user?.userDetail?.phone,
    cpf_cnpj: session?.user?.cpfCnpj
  });
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [userDetail, setUserDetail] = useState();
  const userId = session.user.id;
  const { path } = props;
  const profiles = session?.user?.profileNames || (session?.user && session?.user['profile-names']) || [];

  const formik = useFormik({
    initialValues: {
      password: '',
      newPassword: '',
      confirmPassword: ''
    },
    onSubmit: async values => {
      const payloadParams = {
        password: values.newPassword,
        password_confirmation: values.confirmPassword,
        old_password: values.password
      };
      const { error } = await client.mutate(`/users/${userId}`, payloadParams);
      if (error) {
        toast.error(t('toast.errorUpdatePass'));
      } else {
        toast.success(t('toast.successUpdatePass'));
      }
    },
    validationSchema: yup.object({
      password: yup.string().required(t('profile.typeYourPass')),
      newPassword: yup.string().required(t('profile.typeYourNewPass')),
      confirmPassword: yup
        .string()
        .required(t('profile.confirmNewPass'))
        .oneOf([yup.ref('newPassword')], t('profile.passMustMatch'))
    })
  });

  const updateUser = e => {
    e.preventDefault();
    const payloadParams = {
      ...values,
      userDetailAttributes: {
        id: session.user?.userDetail?.id,
        phone: values.phone,
        hoursByWeek: 40
      }
    };
    delete payloadParams.phone;

    promiseToast({
      url: ['users', session.user.id],
      request: payloadParams,
      successText: t('toast.successUpdateProfile'),
      errorText: t('toast.error')
    })
      .then(data => {
        const newObject = sanitizeResponse(data);
        setSession({ user: newObject });
      })
      .catch(error => {
        toast.error(`Ops, ${error.id} ${error.title}`);
      });
  };

  const handleChange = e => {
    let auxValues = values;
    auxValues[e.target.name] = e.target.value;
    setValues({ ...auxValues });
  };

  const handleChangeImage = e => {
    let file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = _handleReaderLoaded.bind(this);
      reader.readAsBinaryString(file);
    }
  };

  const _handleReaderLoaded = readerEvt => {
    let bynaryString = readerEvt.target.result;
    let auxValues = values;
    auxValues['image'] = `data:image/jpeg;base64,${btoa(bynaryString)}`;
    setValues({ ...auxValues });
    // document.getElementById('profileForm')
    // .dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  };

  const getNotificationSettings = async () => {
    const { data, error } = await client.fetch('notification_settings');
    if (error || data == null) {
      const { data: dataCreation, error: errorCreation } = await client.mutate('notification_settings');
      if (errorCreation || dataCreation == null) {
        toast.error(t('toast.errorGetNotificationSettings'));
      } else {
        notificationsFormik.setValues(dataCreation);
      }
    } else {
      notificationsFormik.setValues(data);
    }
  };

  const updateNotificationSettings = async fields => {
    const requestParams = {
      attributes: {
        ...fields
      }
    };
    const { data, error } = await client.mutate(['notification_settings', notificationsFormik?.values?.id], requestParams);
    if (error || data == null) {
      toast.error(t('toast.errorUpdateNotificationSettings'));
    } else {
      toast.success(t('toast.successUpdateNotifications'));
      notificationsFormik.setValues(data);
    }
  };

  const handleNotificationsChange = fieldName => async newCheckedState => {
    // 1. Atualiza o Formik para o campo específico com o novo estado booleano
    notificationsFormik.setFieldValue(fieldName, newCheckedState);

    // 2. Chame sua função de atualização de settings com o campo e o novo valor
    //    Use 'await' para garantir que a atualização seja enviada após o Formik ser atualizado
    await updateNotificationSettings({
      [fieldName]: newCheckedState // Cria um objeto dinamicamente { 'campo-x': true/false }
    });
  };

  const notificationsFormik = useFormik({
    initialValues: {
      'post-create': true,
      'event-create': true,
      'slot-create': true,
      'activity-create': true,
      'project-create': true,
      'content-create': true
    },
    onSubmit: () => {}
  });

  const updateUserDetail = async body => {
    const payloadParams = {
      hoursByWeek: 40,
      ...body
    };

    const { data, error } = await client.mutate(['user_details', session.user.userDetail.id], payloadParams);

    if (error) {
      toast.error(t('toast.errorUpdateNotificationSettings'));
    } else {
      toast.success(t('toast.successUpdateNotifications'));
      setUserDetail(data);
    }
  };

  const getUserDetail = async () => {
    setLoading(true);
    const { data, error } = await client.fetch('user_details');
    if (error || data == null) {
      toast.error(t('toast.errorGetNotificationSettings'));
    } else {
      setUserDetail(data);
    }
    setLoading(false);
  };

  const updatePassword = async event => {
    event.preventDefault();

    if (newPassword !== confirmPassword) {
    }
    const payloadParams = {
      password: newPassword,
      password_confirmation: confirmPassword,
      old_password: password
    };

    const { error } = await client.mutate(`/users/${userId}`, payloadParams);
    if (error) {
      toast.error(t('toast.errorUpdatePass'));
    } else {
      toast.success(t('toast.successUpdatePass'));
    }
  };

  const [cpfError, setCpfError] = useState(false);
  const handleBlur = () => {
    if (values?.cpf_cnpj?.replaceAll('_', '').length === 14 && !validateCPF(values.cpf_cnpj)) {
      setCpfError(true);
    } else {
      setCpfError(false);
    }
  };

  useEffect(() => {
    getUserDetail();
    getNotificationSettings();
  }, []);

  const breadCrumbs = {
    title: 'Perfil',
    nav: [
      {
        route: '/',
        name: values?.name,
        isActive: true
      }
    ]
  };

  if (loading)
    return (
      <DashboardContent>
        <Loader />
      </DashboardContent>
    );

  return (
    <DashboardContent breadcrumbs={breadCrumbs}>
      <Tabs
        defaultValue="profileInformations"
        className="w-full"
      >
        <TabsList className="mb-8">
          <TabsTrigger value="profileInformations">Informações de perfil</TabsTrigger>
          <TabsTrigger value="notifications">Notificações</TabsTrigger>
          <TabsTrigger value="security">Segurança</TabsTrigger>
        </TabsList>

        <Page className="[&_[role=tabpanel]]:mt-0 [&_[role=tabpanel]]:w-full">
          <PageContent bg>
            <TabsContent value="profileInformations">
              <ProfileInfo>
                <ProfileSidebar>
                  <Label
                    customFileInput
                    className="relative w-fit"
                  >
                    <Input
                      type="file"
                      name="image"
                      id="image"
                      accept=".jpeg, .png, .jpg"
                      onChange={handleChangeImage}
                    />
                    <AvatarWrapper
                      size="lg"
                      src={values.image ?? encodeURI(session.user?.image?.url)}
                      text={values.name ?? session.user?.name}
                      className="border-2 border-solid border-neutral shadow-dropshadow-sm"
                    />
                    <Button
                      size="icon"
                      asChild
                      className="absolute bottom-0 right-0 size-12 border-2 border-solid border-white [&_svg]:size-5"
                    >
                      <span>
                        <FiCamera />
                      </span>
                    </Button>
                  </Label>
                </ProfileSidebar>

                <ProfileForm
                  className="space-y-5"
                  onSubmit={e => updateUser(e)}
                >
                  <FormField
                    label={t('profile.labelFullName')}
                    type="text"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                  />

                  <FormField
                    label="CPF"
                    id="cpf_cnpj"
                    hasError={cpfError}
                    errorText="CPF Inválido"
                  >
                    <InputMask
                      id="cpf_cnpj"
                      name="cpf_cnpj"
                      mask="999.999.999-99"
                      placeholder="Digite o número"
                      value={values.cpf_cnpj}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      aria-invalid={cpfError}
                      disabled={!session.user.profileNames.includes('Administrador') && (session?.user?.cpfCnpj || '') !== ''}
                    />
                  </FormField>

                  <FormField
                    label="Email"
                    type="email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                  />

                  <FormField
                    label={t('profile.labelPhone')}
                    id="phone"
                  >
                    <InputMask
                      id="phone"
                      name="phone"
                      mask="(99) 99999-9999"
                      value={values.phone}
                      onChange={handleChange}
                    />
                  </FormField>

                  <FormField
                    label={t('profile.bio')}
                    id="bio"
                  >
                    <Textarea
                      name="bio"
                      id="bio"
                      value={values.bio}
                      onChange={handleChange}
                    />
                  </FormField>

                  <Button
                    type="submit"
                    variant="third"
                    size="sm"
                  >
                    {t('button.save')}
                  </Button>
                </ProfileForm>
              </ProfileInfo>
            </TabsContent>

            <TabsContent value="notifications">
              <div className="space-y-5">
                <FormCheck
                  label={t('profile.wantSmsNotification')}
                  name="sendPhoneNotifications"
                  id="sendPhoneNotifications"
                  checked={userDetail?.['send-phone-notifications']}
                  onCheckedChange={() =>
                    updateUserDetail({
                      'send-phone-notifications': !userDetail['send-phone-notifications']
                    })
                  }
                />

                <FormCheck
                  label={t('profile.wantEmailNotification')}
                  name="sendEmailNotifications"
                  id="sendEmailNotifications"
                  checked={userDetail?.['send-email-notifications']}
                  onCheckedChange={() =>
                    updateUserDetail({
                      'send-email-notifications': !userDetail['send-email-notifications']
                    })
                  }
                />

                {(userDetail['send-email-notifications'] || userDetail['send-phone-notifications']) && (
                  <>
                    <h3 className="font-bold text-primary-dark">{t('profile.notificatedByTitle')}</h3>

                    <div className="space-y-5">
                      <FormSwitch
                        label={t('profile.switchCreatedEvent')}
                        checked={notificationsFormik?.values?.['event-create']}
                        onCheckedChange={handleNotificationsChange('event-create')}
                      />

                      <FormSwitch
                        label={t('profile.switchNewMaterial')}
                        checked={notificationsFormik?.values?.['content-create']}
                        onCheckedChange={handleNotificationsChange('content-create')}
                      />

                      <FormSwitch
                        label={t('profile.switchNewPost')}
                        checked={notificationsFormik?.values?.['post-create']}
                        onCheckedChange={handleNotificationsChange('post-create')}
                      />

                      <FormSwitch
                        label={t('profile.switchNewActivity')}
                        checked={notificationsFormik?.values?.['activity-create']}
                        onCheckedChange={handleNotificationsChange('activity-create')}
                      />

                      <FormSwitch
                        label={t('profile.switchNewTask')}
                        checked={notificationsFormik?.values?.['slot-create']}
                        onCheckedChange={handleNotificationsChange('slot-create')}
                      />

                      <FormSwitch
                        label={t('profile.switchAddNewProject')}
                        checked={notificationsFormik?.values?.['project-create']}
                        onCheckedChange={handleNotificationsChange('project-create')}
                      />
                    </div>
                  </>
                )}
              </div>
            </TabsContent>

            <TabsContent value="security">
              <form
                className="space-y-5"
                onSubmit={formik.handleSubmit}
              >
                <FormField
                  label={t('profile.currentPass')}
                  id="password"
                  hasError={formik.touched.password && formik.errors.password}
                  errorText={formik.errors.password}
                >
                  <InputPassword
                    id="password"
                    name="password"
                    placeholder={t('profile.typeYourPass')}
                    aria-invalid={Boolean(formik.touched.password && formik.errors.password)}
                    {...formik.getFieldProps('password')}
                  />
                </FormField>

                <FormField
                  label={t('profile.newPass')}
                  id="newPassword"
                  hasError={formik.touched.newPassword && formik.errors.newPassword}
                  errorText={formik.errors.newPassword}
                >
                  <InputPassword
                    id="newPassword"
                    name="newPassword"
                    placeholder={t('profile.typeYourPass')}
                    aria-invalid={Boolean(formik.touched.newPassword && formik.errors.newPassword)}
                    {...formik.getFieldProps('newPassword')}
                  />
                </FormField>

                <FormField
                  label={t('profile.confirmYourPass')}
                  id="confirmPassword"
                  hasError={formik.touched.confirmPassword && formik.errors.confirmPassword}
                  errorText={formik.errors.confirmPassword}
                >
                  <InputPassword
                    id="confirmPassword"
                    name="confirmPassword"
                    placeholder={t('profile.typeYourPass')}
                    aria-invalid={Boolean(formik.touched.confirmPassword && formik.errors.confirmPassword)}
                    {...formik.getFieldProps('confirmPassword')}
                  />
                </FormField>

                <Button
                  type="submit"
                  variant="third"
                  size="sm"
                >
                  {t('button.save')}
                </Button>
              </form>
            </TabsContent>
          </PageContent>

          <PageSidebar variant="md">
            <TypographyDisclaimer>
              {t('profile.disclaimerLGPD')}{' '}
              <a
                className="hover:underline"
                href="/termos-de-uso"
                target="_blank"
                rel="noopener noreferrer"
              >
                termos de uso
              </a>{' '}
              e{' '}
              <a
                className="hover:underline"
                href="/politica-de-privacidade"
                target="_blank"
                rel="noopener noreferrer"
              >
                política de privacidade
              </a>
              .
            </TypographyDisclaimer>
          </PageSidebar>
        </Page>
      </Tabs>

      <Onboarding
        show={showOnboarding}
        setShowOnboarding={setShowOnboarding}
        onClose={() => setShowOnboarding(false)}
        path={path}
        profiles={profiles}
        title={t('onboarding.titleProfile')}
      />
    </DashboardContent>
  );
}
